import { Measurement, MeasurementContext, SiUnits, UnitType, UsUnits } from "src/app/shared/data/engineering-enums";
import { ActivatedRoute } from "@angular/router";
import { ApiService } from "src/app/shared/services/api.service";
import { EngineeringToolsApiService } from "src/app/shared/services/engineering-tools-api";
import { UnitConverterService } from "src/app/shared/services/unit-converter.service";

export class ToolBaseComponent {
    constructor(
      protected hrtApi: ApiService,
      protected api: EngineeringToolsApiService,
      protected unitConverter: UnitConverterService
    ) { 
  }

  createComponent() {
    this.api.CreateComponent(this.componentType)
      .subscribe(vm => {
        this.viewModel = vm;
        //console.log(JSON.stringify(vm));
        this.isLoaded = true;
      });
  }

  initializeFromRouteIdOrCreate(route: ActivatedRoute) {
    const routeLoadId = Number(route.snapshot.paramMap.get('id'));

    if (Number.isInteger(routeLoadId) && routeLoadId > 0) {
      this.loadId = routeLoadId;
      this.api.GetComponentSaves(this.hrtApi.UserId, this.componentType).subscribe({
        next: (items) => {
          this.loadItems = items ?? [];
          this.loadCalculation();
        },
        error: () => {
          this.loadItems = [];
          this.loadCalculation();
        },
      });
      return;
    }

    this.createComponent();
  }

  updateComponent(setCalculate: boolean = true) {
    this.showLoadingMessage("Calculating...", setCalculate);
    this.api.UpdateComponent(this.componentType, this.viewModel)
      .subscribe(vm => {
        this.viewModel = vm;
        //console.log(JSON.stringify(vm));
        this.hideLoadingMessage();
      });
  }

  public MeasurementContext = MeasurementContext;
  
  componentType: string = "";

  viewModel: any = {};

  isLoaded: boolean = false;
  isUsUnits: boolean = true;
  loadingMessageVisible: boolean = false;
  loadingMessage: string = "Loading...";

  formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'UTC' }) + ' ' + new Date(dateStr).toLocaleTimeString('en-US', { timeZone: 'UTC' });
  }

  formatUnits(value: any, context: MeasurementContext, decimalPlaces: number) {
    if (this.isUsUnits)
      return this.unitConverter.FormatUs(value, context, decimalPlaces, false);
    return this.unitConverter.FormatSi(value, context, decimalPlaces, false);
  }

  getUnitText(context: MeasurementContext) {
    if (this.isUsUnits)
      return this.unitConverter.GetUnitText(UsUnits.get(context) || UnitType.F);
    return this.unitConverter.GetUnitText(SiUnits.get(context) || UnitType.C);
  }

  formatMeasurementToDisplay(value: Measurement, context: MeasurementContext, decimalPlaces: number): string {
    if (this.isUsUnits) {
      return this.unitConverter.Convert(value.Unit, UsUnits.get(context) || UnitType.F, value.Value).toFixed(decimalPlaces);
    }
    return this.unitConverter.Convert(value.Unit, SiUnits.get(context) || UnitType.C, value.Value).toFixed(decimalPlaces);
  }

  formatMeasurementFromDisplay(value: string, measurement: Measurement, context: MeasurementContext): number {
    if (this.isUsUnits) {
      return this.unitConverter.Convert(UsUnits.get(context) || UnitType.F, measurement.Unit, parseFloat(value));
    }
    return this.unitConverter.Convert(SiUnits.get(context) || UnitType.C, measurement.Unit, parseFloat(value));
  }

  showLoadingMessage(message: string = "Loading...", show: boolean = true) {
    this.loadingMessage = message;
    this.loadingMessageVisible = show;
  }

  hideLoadingMessage() {
    this.loadingMessageVisible = false;
  }

  private applyLoadedCalculationMetadata(loadedCalculation?: any) {
    this.loadedCalculation = loadedCalculation ?? {
      Id: this.loadId,
      Name: this.saveName,
      ProjectNumber: this.saveProjectNumber,
      Comments: this.saveComments,
    };
    this.saveName = this.loadedCalculation.Name ?? '';
    this.saveProjectNumber = this.loadedCalculation.ProjectNumber ?? '';
    this.saveComments = this.loadedCalculation.Comments ?? '';
  }

  showSaveDialog: boolean = false;
  showLoadDialog: boolean = false;
  saveName: string = '';
  saveProjectNumber: string = '';
  saveComments: string = '';
  loadProjectNumber: string = '';
  loadProjectNumberTimeout: any = null;
  loadItems: any[] = [];
  loadId: number = 0;
  loadedCalculation: any = null;

  getDefaultSaveName() : string {
    return "";
  }

  openSaveDialog() {
    if (!this.saveName) {
      this.saveName = this.getDefaultSaveName();
    }
    this.showSaveDialog = true;
  }

  saveCalculationAsNew() {
    this.showSaveDialog = false;
    this.showLoadingMessage("Saving calculation...");
    this.api.SaveComponent(this.hrtApi.UserId, this.componentType, this.viewModel, this.saveName, this.saveProjectNumber, this.saveComments)
      .subscribe(response => {
        this.loadId = response;
        this.loadedCalculation = {
          Id: this.loadId,
          Name: this.saveName,
          ProjectNumber: this.saveProjectNumber,
          Comments: this.saveComments
        };
        this.hideLoadingMessage();
      });
  }

  saveCalculation() {
    this.showLoadingMessage("Saving calculation...");
    this.api.SaveComponent(this.hrtApi.UserId, this.componentType, this.viewModel, this.saveName, this.saveProjectNumber, this.saveComments, this.loadedCalculation.Id)
      .subscribe(response => {
        this.hideLoadingMessage();
      });
  }

  openLoadDialog() {
    this.showLoadingMessage("Loading list of calculations...");
    this.getLoadItems();
  }

  getLoadItems() {
    this.api.GetComponentSaves(this.hrtApi.UserId, this.componentType, this.loadProjectNumber)
      .subscribe(items => {
        this.loadItems = items;
        this.showLoadDialog = true;
        this.hideLoadingMessage();
      });
  }

  loadProjectNumberChanged() {
    console.log("loadProjectNumberChanged");
    if (this.loadProjectNumberTimeout) {
      clearTimeout(this.loadProjectNumberTimeout);
    }
    this.loadProjectNumberTimeout = setTimeout(() => {
      this.getLoadItems();
    }, 500);
  }

  loadCalculation() {
    this.showLoadDialog = false;
    this.showLoadingMessage("Loading calculation...");
    this.api.LoadComponent(this.loadId)
      .subscribe(vm => {
        this.viewModel = vm;
        this.applyLoadedCalculationMetadata(
          this.loadItems.find(x => x.Id === this.loadId)
        );
        this.isLoaded = true;
        this.hideLoadingMessage();
      });
  }

  exportDebugDataToCsv() {
    this.api.ExportDebugData(this.componentType, this.viewModel).subscribe((data: string) => {
      const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const dateTime = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}_${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}_${now.getMinutes().toString().padStart(2, '0')}_${now.getSeconds().toString().padStart(2, '0')}`;
      a.download = `${this.componentType}_DebugData_${dateTime}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }
}